/**
 * useGamificationRewards — fire-and-forget gamification events from UI actions.
 *
 * Thin wrapper that completes quests and awards XP without blocking the
 * calling UI. Every call is idempotent on the backend.
 */
import { useCallback, useRef } from 'react';
import config from '../utils/config';
import { getUser } from '../utils/helpers';

const BASE = config.SERVER_BASE_URL || '';

function userId() {
  try {
    const u = getUser();
    return u?.id || u?.userId || u?.email || null;
  } catch { return null; }
}

export default function useGamificationRewards() {
  // Track which quests we've already fired this session to avoid duplicate calls
  const fired = useRef(new Set());

  const completeQuest = useCallback((questId) => {
    if (fired.current.has(questId)) return;
    const uid = userId();
    if (!uid) return;
    fired.current.add(questId);
    fetch(`${BASE}/api/gamification/quest/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-user-id': uid },
      body: JSON.stringify({ questId }),
    }).catch(() => {}); // fire-and-forget
  }, []);

  const awardXp = useCallback((xp, reason) => {
    const uid = userId();
    if (!uid) return;
    fetch(`${BASE}/api/gamification/award`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-user-id': uid },
      body: JSON.stringify({ xp, reason }),
    }).catch(() => {});
  }, []);

  /** Call when user opens / views the Agent Builder canvas */
  const rewardCanvasExplore = useCallback(() => completeQuest('explore_canvas'), [completeQuest]);

  /** Call when user deploys or creates an agent */
  const rewardAgentCreation = useCallback(() => {
    completeQuest('first_agent');
    awardXp(5, 'agent_deployed');
  }, [completeQuest, awardXp]);

  /** Call when user selects a template from the playbook */
  const rewardTemplateSelect = useCallback(() => {
    awardXp(2, 'template_selected');
  }, [awardXp]);

  return { completeQuest, awardXp, rewardCanvasExplore, rewardAgentCreation, rewardTemplateSelect };
}
